import { DataSource } from "@angular/cdk/collections";
import { BehaviorSubject, catchError, finalize, Observable, of } from "rxjs";
import { ResultList } from "../models";

/**
 * A datasource for e.g. mat-table that uses an observable as input.
 */
 export class ObservableDataSource<T> implements DataSource<T> {
  private readonly dataSubject = new BehaviorSubject<T[]>([]);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);

  /**
   * Use to show a loading indicator, for example:
   * `<div class="spinner-container" *ngIf="companiesDataSource.loading$ | async">
        <mat-spinner></mat-spinner>
      </div>`
   */
  public loading$ = this.loadingSubject.asObservable();

  /**
   * Create a new ObservableDataSource.
   * 
   * @param observable$ The source observable which delivers type <T> or a ResultList of type <T>
   * @param omitDisconnect If true, the automatic disconnect (on table destroy) is omitted.
   */
  constructor(observable$: Observable<T[] | ResultList<T>>, private omitDisconnect = false) {
    this.load(observable$);
  }

  update(observable$: Observable<T[] | ResultList<T>>) {
    this.load(observable$);
  }

  connect(): Observable<T[]> {
    return this.dataSubject.asObservable();
  }

  pushValue(value: T) {
    const result = this.dataSubject.value;
    result.push(value);
    this.dataSubject.next(result);
  }

  unshiftValue(value: T) {
    const result = this.dataSubject.value;
    result.unshift(value);
    this.dataSubject.next(result);
  }

  disconnect(): void {
    if (!this.omitDisconnect) {
      this.doDisconnect();
    }
  }

  doDisconnect(): void {
    this.dataSubject.complete();
    this.loadingSubject.complete();
  }

  private load(observable$: Observable<T[] | ResultList<T>>) {
    this.loadingSubject.next(true);
    observable$
      .pipe(
        catchError(() => of({ result: [] })),
        finalize(() => this.loadingSubject.next(false))
      )
      .subscribe((result) => {
        let data = result;
        // check if data is a ResultList
        if ('result' in result) {
          data = result.result;
          // TODO: what about the count?
        }
        this.dataSubject.next(data as T[]);
      });
  }
}
